"use client";

import { ReactNode } from "react";
import { useTranslations } from "next-intl";
import { getAssetUrl } from "@/utils/assetUrl";
import {
  TransitionBlockType,
  TransitionContentBlock,
  RichTextBlock,
  ImageBlock,
  SourceBlock,
} from "@/commonTypes/transitionContentBlocks";

/** Tag renderers shared by all rich-text-enabled blocks */
const richTextTags = {
  bold: (chunks: ReactNode) => <strong>{chunks}</strong>,
  sup: (chunks: ReactNode) => <sup>{chunks}</sup>,
  ul: (chunks: ReactNode) => <ul>{chunks}</ul>,
  li: (chunks: ReactNode) => <li>{chunks}</li>,
  br: () => <br />,
};

/** Extended tags for source blocks — adds line breaks and hyperlinks */
const sourceRichTextTags = {
  ...richTextTags,
  br: () => <br />,
  sourceLink: (chunks: ReactNode) => (
    <a
      href={String(chunks)}
      target="_blank"
      rel="noopener noreferrer"
      className="transition-source-link"
    >
      {chunks}
    </a>
  ),
};

function RichTextBlockRenderer({ block }: { block: RichTextBlock }) {
  const t = useTranslations();
  return (
    <div className="transition-body-text">
      {t.rich(block.translationKey, richTextTags)}
    </div>
  );
}

function ImageBlockRenderer({ block }: { block: ImageBlock }) {
  const t = useTranslations();
  const imageSrc = getAssetUrl(`/images/transitions/${block.imageKey}.webp`);
  return (
    <div className="transition-image-container">
      <img
        src={imageSrc}
        alt={t(block.altTranslationKey)}
        className="transition-image"
      />
    </div>
  );
}

function SourceBlockRenderer({ block }: { block: SourceBlock }) {
  const t = useTranslations();
  return (
    <p className="transition-source">
      {t.rich(block.translationKey, sourceRichTextTags)}
    </p>
  );
}

export default function TransitionBlockRenderer({
  block,
}: {
  block: TransitionContentBlock;
}) {
  switch (block.type) {
    case TransitionBlockType.RICH_TEXT:
      return <RichTextBlockRenderer block={block} />;
    case TransitionBlockType.IMAGE:
      return <ImageBlockRenderer block={block} />;
    case TransitionBlockType.SOURCE:
      return <SourceBlockRenderer block={block} />;
    default:
      return null;
  }
}
